<script lang="ts">
  import type { BulkVerifyResultItem } from '../../lib/bulkAiVerification';

  export let open = false;
  export let progress = 0;
  export let processed = 0;
  export let total = 0;
  export let completed = 0;
  export let failed = 0;
  export let status: 'starting' | 'running' | 'completed' | 'failed' =
    'running';
  export let message =
    'Please wait while AI verifies the records in the background.';
  export let results: BulkVerifyResultItem[] = [];

  $: sortedResults = [...(results || [])].sort(
    (a, b) => Number(a.id) - Number(b.id),
  );

  const handleClose = () => {
    open = false;
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  function handleKeyDown(event: KeyboardEvent) {
    if (open && event.key === 'Escape') {
      handleClose();
    }
  }
</script>

<svelte:window on:keydown={handleKeyDown} />

{#if open}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 opacity-100 backdrop-blur-sm transition-opacity duration-300"
  >
    <div
      class="flex max-h-[90vh] w-11/12 max-w-lg flex-col rounded-xl bg-white p-6 shadow-2xl transition-all"
    >
      <div class="mb-4 flex shrink-0 items-center justify-between">
        <div class="flex items-center space-x-3">
          {#if status === 'completed'}
            <span
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600"
            >
              <svg
                class="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </span>
          {:else if status === 'failed'}
            <span
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600"
            >
              <svg
                class="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </span>
          {:else}
            <span
              class="flex h-9 w-9 shrink-0 animate-pulse items-center justify-center rounded-full bg-blue-100 text-blue-600"
            >
              <svg class="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </span>
          {/if}
          <div>
            <h3 class="text-lg font-semibold text-gray-900">
              {#if status === 'completed'}
                AI Verification Complete
              {:else if status === 'failed'}
                Verification Encountered an Issue
              {:else}
                Running AI Verification...
              {/if}
            </h3>
            <p class="text-xs text-gray-500">
              {#if total > 0}
                {processed} of {total} records processed ({Math.floor(
                  progress,
                )}%)
              {:else}
                Queuing tasks...
              {/if}
            </p>
          </div>
        </div>

        <button
          type="button"
          on:click={handleClose}
          class="shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          title="Close modal"
        >
          <svg
            class="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- GRUIM styled Progress Bar -->
      <div class="my-3 shrink-0">
        <div
          class="h-4 w-full overflow-hidden rounded-full bg-gray-200 shadow-inner"
        >
          <div
            class="h-full rounded-full bg-blue-600 text-center text-[10px] font-semibold leading-4 text-white transition-all duration-300 ease-out"
            style="width: {Math.max(progress, 0)}%"
          >
            {#if progress >= 8}
              {`${Math.floor(progress)}%`}
            {/if}
          </div>
        </div>
      </div>

      <!-- Counters & Subtitle -->
      <div
        class="flex shrink-0 items-center justify-between text-xs text-gray-600"
      >
        <div class="max-w-[320px] truncate">
          {message}
        </div>
        {#if total > 0}
          <div class="flex shrink-0 items-center space-x-2 font-medium">
            {#if completed > 0}
              <span class="text-green-600">✓ {completed}</span>
            {/if}
            {#if failed > 0}
              <span class="text-red-500">✗ {failed}</span>
            {/if}
          </div>
        {/if}
      </div>

      <!-- Process Decisions List -->
      {#if results && results.length > 0}
        <div
          class="mt-4 flex min-h-0 flex-1 flex-col border-t border-gray-100 pt-3"
        >
          <div class="mb-2 flex shrink-0 items-center justify-between">
            <span
              class="text-xs font-semibold uppercase tracking-wider text-gray-500"
            >
              Process Decisions ({results.length})
            </span>
            <span class="text-[11px] text-gray-400">
              {results.filter((r) => r.decision === 'APPROVABLE').length} approvable
              · {results.filter((r) => r.decision === 'NOT_APPROVABLE').length} not
              approvable
            </span>
          </div>

          <div
            class="max-h-52 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/50"
          >
            {#each sortedResults as item (item.id)}
              <div
                class="flex items-center justify-between px-3 py-2 text-xs transition-colors hover:bg-white"
              >
                <div class="flex min-w-0 items-center space-x-2">
                  <span class="shrink-0 font-mono font-semibold text-gray-800"
                    >#{item.id}</span
                  >
                  {#if item.status === 'failed'}
                    <span
                      class="max-w-[200px] truncate text-[11px] text-red-500"
                      title={item.error || 'Failed'}
                    >
                      {item.error || 'Failed'}
                    </span>
                  {/if}
                </div>
                <div class="ml-2 shrink-0">
                  {#if item.decision === 'APPROVABLE'}
                    <span
                      class="inline-flex items-center rounded-full border border-green-200 bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-800"
                    >
                      <svg
                        class="mr-1 h-3 w-3 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2.5"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      APPROVABLE
                    </span>
                  {:else if item.decision === 'NOT_APPROVABLE'}
                    <span
                      class="inline-flex items-center rounded-full border border-red-200 bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-800"
                    >
                      <svg
                        class="mr-1 h-3 w-3 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2.5"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      NOT APPROVABLE
                    </span>
                  {:else if item.status === 'failed'}
                    <span
                      class="inline-flex items-center rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
                    >
                      FAILED
                    </span>
                  {:else}
                    <span
                      class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600"
                    >
                      {item.decision || 'PENDING'}
                    </span>
                  {/if}
                </div>
              </div>
            {/each}
          </div>
        </div>
      {/if}

      <!-- Close button for completed or failed states -->
      {#if status === 'completed' || status === 'failed'}
        <div class="mt-4 shrink-0">
          <button
            type="button"
            on:click={handleClose}
            class="inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 active:bg-blue-800"
          >
            Close
          </button>
        </div>
      {/if}
    </div>
  </div>
{/if}
